import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { api, formatMoney, formatApiError } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const { currency, rateHuf } = useCurrency(); // subscribe so prices re-render on currency switch
  const navigate = useNavigate();
  const [po, setPo] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        po_number: po,
        notes,
        // Tell the backend which currency the customer is looking at — they
        // expect the order receipt to come back in the same one. We send the
        // HUF rate too so the server's conversion matches what the customer
        // saw on the cart page.
        currency,
        huf_rate: currency === "HUF" ? rateHuf : 0,
      };
      const { data } = await api.post("/orders", payload);
      setDone(data);
      clear();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <Navbar />
        <div className="breadcrumb">
          <div className="max-w-[1200px] mx-auto px-4">
            <Link to="/catalog">Kezdőlap</Link> / <span style={{ color: "var(--text)" }}>Rendelés visszaigazolva</span>
          </div>
        </div>
        <main className="max-w-[700px] mx-auto px-4 py-10 text-center" data-testid="order-success">
          <div className="card-elev">
            <div className="section-title justify-center">A rendelés sikeresen rögzítve</div>
            <div className="p-8">
              <div
                className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center"
                style={{ background: "rgba(42,140,42,0.12)" }}
              >
                <Check size={26} style={{ color: "var(--in-stock)" }} />
              </div>
              <h1 className="text-xl font-bold mb-3 uppercase">Köszönjük a rendelést!</h1>
              <p className="text-sm mb-2" style={{ color: "var(--text-mute)" }}>
                Hivatkozási szám:{" "}
                <span className="font-mono font-bold" style={{ color: "var(--text)" }}>
                  #{done.id.slice(0, 8).toUpperCase()}
                </span>
              </p>
              <p className="text-sm mb-6" style={{ color: "var(--text-mute)" }}>
                Rendelési érték:{" "}
                <span className="font-bold" style={{ color: "var(--brand)" }}>{formatMoney(done.total)}</span>{" "}
                — kollégánk hamarosan visszaigazolja a készletet és a szállítási költséget.
              </p>
              <div className="flex justify-center gap-2 flex-wrap">
                <Link to="/orders" className="btn-primary" data-testid="view-orders-link">
                  Rendelések megtekintése <ChevronRight size={14} />
                </Link>
                <Link to="/catalog" className="btn-ghost">Vásárlás folytatása</Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div>
        <Navbar />
        <main className="max-w-[700px] mx-auto px-4 py-16 text-center">
          <div className="card-elev p-10">
            <p className="text-sm mb-4" style={{ color: "var(--text-mute)" }}>
              A kosár üres.
            </p>
            <Link to="/catalog" className="btn-primary">Tovább a katalógushoz</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Kezdőlap</Link> / <Link to="/cart">Bevásárlókosár</Link> /{" "}
          <span style={{ color: "var(--text)" }}>Pénztár</span>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide">Pénztár</h1>
        <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

        <div className="grid lg:grid-cols-[1fr_360px] gap-5 items-start">
          <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
            <section className="card-elev">
              <div className="section-title">Számlázási adatok</div>
              <div className="p-5 grid sm:grid-cols-2 gap-4 text-sm">
                <Detail label="Vevő neve" value={user?.name || "—"} />
                <Detail label="E-mail" value={user?.email} />
                <Detail label="Cégnév / Műhely" value={user?.company || "—"} />
                <Detail label="Adószám" value={user?.tax_id || "—"} />
                <Detail label="Számlázási cím" value={
                  user?.street || user?.city
                    ? `${user?.postal_code || ""} ${user?.city || ""}, ${user?.street || ""}`
                    : "—"
                } />
                <Detail label="Telefon" value={user?.phone || "—"} />
              </div>
            </section>

            <section className="card-elev">
              <div className="section-title">Rendelés részletei</div>
              <div className="p-5 space-y-4">
                <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
                  <label className="text-sm font-bold">Alvázszám (VIN):</label>
                  <input
                    className="input"
                    value={po}
                    onChange={(e) => setPo(e.target.value.toUpperCase())}
                    placeholder="opcionális, pl. WAUZZZ8K0AA123456 (17 karakter)"
                    maxLength={17}
                    data-testid="po-number-input"
                  />
                </div>
                <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                  <label className="text-sm font-bold pt-2">Szállítási megjegyzés:</label>
                  <textarea
                    className="input"
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Speciális kezelés, kért szállítási dátum..."
                    data-testid="notes-input"
                  />
                </div>
              </div>
            </section>

            <div className="flex justify-between">
              <Link to="/cart" className="btn-ghost"><ChevronLeft size={12} /> Vissza a kosárhoz</Link>
            </div>
          </form>

          <aside className="card-elev overflow-hidden lg:sticky lg:top-4">
            <div className="section-title">A rendelés tartalma</div>
            <div className="p-4 space-y-2 text-[13px] max-h-72 overflow-auto pr-1">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-2 border-b pb-1.5" style={{ borderColor: "var(--border)" }}>
                  <span className="truncate">
                    <span className="font-bold" style={{ color: "var(--text-mute)" }}>{i.quantity}× </span>
                    <span className="font-bold">{i.name}</span>
                  </span>
                  <span className="font-mono shrink-0">{formatMoney(i.unit_price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t flex justify-between items-baseline" style={{ borderColor: "var(--border)" }}>
              <span className="font-bold uppercase">Összesen:</span>
              <span className="font-bold text-lg" style={{ color: "var(--brand)" }}>{formatMoney(total)}</span>
            </div>
            <div className="p-4 pt-0">
              <button
                onClick={submit}
                disabled={loading}
                className="btn-primary w-full"
                data-testid="submit-order-button"
              >
                {loading ? "Küldés..." : (<>Rendelés leadása <ChevronRight size={14} /></>)}
              </button>
              <p className="text-[11px] mt-3 leading-relaxed" style={{ color: "var(--text-mute)" }}>
                Most nem történik fizetés. Az alkatrészvezető megerősíti a készletet és a szállítási költséget.
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase font-bold mb-0.5" style={{ color: "var(--text-mute)" }}>{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
